import React, { useEffect, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import qs from 'query-string';

// Components
import SplashScreen from 'components/SplashScreen';

// Pages
import AutoFaraAuthLogin from 'pages/Auth/FaraAuth';
const Rules = React.lazy(() => import('pages/Rules/Rules'));
const AuthEntryPoint = React.lazy(() => import('pages/Auth/AuthEntryPoint'));
const OnBoardingPage = React.lazy(() => import('pages/OnBoarding/OnBoarding'));
const ApplicationRouting = React.lazy(() => import('pages'));

// Redux Actions
import { signOut, setRedirectUrl, clearRedirectUrl } from 'redux/slice/login';
import { signOutWallet } from 'redux/slice/walletRequestStepOne';

// Utils
import { decodeQueryStrings } from 'services/decodeQueryStrings';

/**
 * @typedef {Object} RouterProps
 * @property {string} tokenUser - User's authentication token
 * @property {number} stepUser - Current user step
 * @property {boolean} isRegistered - User registration status
 * @property {string} logout_callback - Callback URL for logout
 */

/**
 * Wraps a component with Suspense and SplashScreen
 * @param {React.ComponentType} Component - Component to wrap
 * @param {Object} props - Props to pass to the component
 */
const SuspenseWrapper = ({ Component, ...props }) => (
	<Suspense fallback={<SplashScreen />}>
		<Component {...props} />
	</Suspense>
);

/**
 * Main Router component for the application
 */
const Router = () => {
	const { tokenUser, stepUser, isRegistered, logout_callback, redirectUrl } = useSelector((state) => ({
		tokenUser: state?.login?.token,
		stepUser: state?.login?.step,
		isRegistered: state.login?.isRegistered,
		logout_callback: state.login?.logout_callback,
		redirectUrl: state.login?.redirectUrl,
	}));

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	// Parse query parameters
	const parseLocation = qs.parse(location.search, {
		parseNumbers: true,
		parseBooleans: true,
	});

	// Authentication check effect
	useEffect(() => {
		const isTokenInvalid = !(tokenUser && tokenUser !== 'null');
		if (isTokenInvalid) {
			// Preserve redirect URL before clearing localStorage
			const redirectUrl = localStorage.getItem('redirectUrl');

			dispatch(signOut());
			dispatch(signOutWallet());
			localStorage.clear();

			// Restore redirect URL after clearing
			if (redirectUrl) {
				localStorage.setItem('redirectUrl', redirectUrl);
			}
		}
	}, [dispatch, tokenUser]);

	// Capture intended destination for redirect after login
	useEffect(() => {
		const isAuthenticated = isRegistered && tokenUser && tokenUser !== 'null';
		const publicRoutes = ['/lac', '/', '/auth/login', '/rules', '/home', '/fara-auth/login'];

		// If user is not authenticated and trying to access a protected route
		// AND we don't already have a redirect URL (to avoid overriding pre-captured URLs)
		if (!isAuthenticated && !publicRoutes.includes(pathname) && !redirectUrl) {
			// Use window.location instead of location to get the full URL
			const fullWindowUrl = window.location.href;
			const url = new URL(fullWindowUrl);
			const searchParams = new URLSearchParams(url.search);

			// Remove identity parameter if it exists
			searchParams.delete('identity');
			const cleanSearch = searchParams.toString();
			const fullUrl = url.pathname + (cleanSearch ? `?${cleanSearch}` : '');

			dispatch(setRedirectUrl(fullUrl));
			// Also store in localStorage as backup
			localStorage.setItem('redirectUrl', fullUrl);
		}
	}, [dispatch, pathname, isRegistered, tokenUser, redirectUrl]);

	// Capture query parameters from localStorage if they exist
	useEffect(() => {
		const isAuthenticated = isRegistered && tokenUser && tokenUser !== 'null';
		const publicRoutes = ['/lac', '/', '/auth/login', '/rules', '/home', '/fara-auth/login'];

		// Check if we have a stored redirect URL in localStorage
		const storedRedirectUrl = localStorage.getItem('redirectUrl');

		// If we have a stored redirect URL and it's different from current redirect URL
		if (!isAuthenticated && storedRedirectUrl && storedRedirectUrl !== redirectUrl) {
			dispatch(setRedirectUrl(storedRedirectUrl));
		}
	}, [dispatch, pathname, isRegistered, tokenUser, redirectUrl]);

	// Identity parameter effect
	useEffect(() => {
		const publicRoutes = ['/lac', '/', '/auth/login', '/rules', '/home', '/fara-auth/login'];
		if (publicRoutes.includes(pathname)) {
			const randomIdentity =
				Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
			navigate({
				search: decodeQueryStrings({
					...parseLocation,
					identity: randomIdentity,
				}),
			});
		}
	}, [pathname]);

	// Logout callback effect
	useEffect(() => {
		const shouldRedirect = logout_callback && tokenUser === 'null';
		if (shouldRedirect) {
			window.location.href = logout_callback;
		}
	}, [logout_callback, tokenUser]);

	const isAuthenticated = isRegistered && tokenUser && tokenUser !== 'null';
	const isStep3User = stepUser === 3 && isRegistered === false;

	// Check localStorage on Router mount
	React.useEffect(() => {
		const storedRedirectUrl = localStorage.getItem('redirectUrl');

		// If we have a stored redirect URL and no current redirect URL, restore it
		if (storedRedirectUrl && !redirectUrl) {
			dispatch(setRedirectUrl(storedRedirectUrl));
		}
	}, []); // Only run once on mount

	// Handle redirect after authentication
	React.useEffect(() => {
		if (isAuthenticated && redirectUrl && pathname === '/auth/login') {
			// Parse the redirect URL to separate pathname and search
			const url = new URL(redirectUrl, window.location.origin);

			// Use navigate with object format to preserve query parameters
			navigate({
				pathname: url.pathname,
				search: url.search
			});

			// Clear redirect URL and flag after successful navigation
			dispatch(clearRedirectUrl());
			localStorage.removeItem('redirectUrl');
		}
	}, [isAuthenticated, redirectUrl, pathname, dispatch, navigate]);


	return (
		<Routes>
			{/* Public Routes */}
			<Route path="/lac" element={<SuspenseWrapper Component={AuthEntryPoint} loginFrom />} />

			{!isRegistered && (
				<>
					<Route path="/" element={<SuspenseWrapper Component={OnBoardingPage} />} />
					<Route path="/auth/login" element={<SuspenseWrapper Component={AuthEntryPoint} />} />
					<Route path="/rules" element={<SuspenseWrapper Component={Rules} />} />
				</>
			)}

			{/* Step 3 User Route */}
			{isStep3User && <Route path="/auth/login" element={<SuspenseWrapper Component={AuthEntryPoint} />} />}

			{/* Protected Routes */}
			{isAuthenticated && <Route path="/*" element={<SuspenseWrapper Component={ApplicationRouting} />} />}

			{/* Fallback Route */}
			<Route path="/*" element={<Navigate to={isAuthenticated ? '/home' : '/auth/login'} />} />

			{/* Special Routes */}
			<Route path="fara-auth/login" element={<AutoFaraAuthLogin />} />
		</Routes>
	);
};

export default Router;
